// modules/elearning/subjects/SubjectFilter.jsx
import React, { useState, useEffect } from "react";
import {
  Search,
  RotateCw,
  SlidersHorizontal,
  Plus,
  ChevronDown,
  Eraser,
  BookOpen,
  MoreVertical,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useDebounce } from "use-debounce";
import { hasPermission } from "../../../utils/permissionUtils";
import MobileAdvancedFilterSheet from "../../../components/ui/MobileAdvancedFilterSheet";

const SubjectFilter = ({ onNewClick, onFilterChange }) => {
  const [advanced, setAdvanced] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // ---------------- FILTER STATE ----------------
  const [filters, setFilters] = useState({
    keyword: "",
    status: "",
  });

  const statusOptions = [
    { value: "active", label: "Đang sử dụng" },
    { value: "inactive", label: "Ngưng sử dụng" },
    { value: "draft", label: "Sắp triển khai" },
  ];

  const isFiltering = Object.values(filters).some((v) => v !== "");

  // ---------------- RESPONSIVE ----------------
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ---------------- DEBOUNCE KEYWORD ----------------
  const [debouncedKeyword] = useDebounce(filters.keyword, 500);

  useEffect(() => {
    onFilterChange?.({ ...filters, keyword: debouncedKeyword });
  }, [debouncedKeyword]);

  // ---------------- UPDATE FILTER ----------------
  const updateFilter = (name, value) => {
    const updated = { ...filters, [name]: value };
    setFilters(updated);
    if (name !== "keyword") onFilterChange?.(updated);
  };

  const clearFilters = () => {
    const cleared = { keyword: "", status: "" };
    setFilters(cleared);
    onFilterChange?.(cleared);
  };

  const openGuide = () => {
    const guideUrl = `${window.location.origin}/guides/elearning-subjects-guide.html`;
    window.open(guideUrl, "_blank", "noopener,noreferrer");
  };

  // ================= RENDER COMPONENTS =================
  const ActionButton = ({ icon: Icon, onClick, title, active }) => (
    <button
      onClick={onClick}
      title={title}
      className={`h-11 w-11 flex items-center justify-center rounded-xl border transition-all duration-200 
        ${active
          ? "bg-red-50 border-red-200 text-red-600 shadow-sm"
          : "bg-white border-gray-200 text-gray-500 hover:border-red-400 hover:text-red-500"
        }`}
    >
      <Icon size={18} />
    </button>
  );

  const SelectBox = ({ label, value, options, onChange }) => (
    <div className="flex flex-col gap-1.5 w-full">
      <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">
        {label}
      </label>
      <div className="relative group">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="appearance-none w-full bg-white border border-gray-200 text-gray-700 py-2.5 px-4 pr-10 rounded-xl text-sm
                     focus:outline-none focus:border-red-500 focus:ring-4 focus:ring-red-500/5 transition-all cursor-pointer font-medium shadow-sm"
        >
          <option value="">Tất cả {label}</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <ChevronDown size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none group-hover:text-red-500 transition-colors" />
      </div>
    </div>
  );

  // =============================================================================
  //                                   MOBILE UI
  // =============================================================================
  if (isMobile) {
    return (
      <div className="mb-6 space-y-4">
        <div className="flex gap-2 text-left">
          <div className="relative flex-1 group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors" size={18} />
            <input
              type="text"
              placeholder="Tìm tên hoặc mã môn học..."
              value={filters.keyword}
              onChange={(e) => updateFilter("keyword", e.target.value)}
              className="w-full bg-white border border-gray-200 pl-11 pr-4 py-3 rounded-2xl focus:outline-none focus:border-red-500 shadow-sm transition-all outline-none"
            />
          </div>
          <ActionButton icon={SlidersHorizontal} onClick={() => setAdvanced(true)} active={advanced} />
        </div>

        {/* FLOATING ADD */}
        {hasPermission("elearning.subjects", "C") && (
          <button
            onClick={onNewClick}
            className="fixed bottom-24 right-6 bg-red-600 text-white w-14 h-14 rounded-full shadow-lg shadow-red-200 flex items-center justify-center hover:scale-105 active:scale-95 transition-all z-40"
          >
            <Plus size={24} />
          </button>
        )}

        <MobileAdvancedFilterSheet
          open={advanced}
          onClose={() => setAdvanced(false)}
          title="Bộ lọc môn học"
          footer={
            <>
              <button
                type="button"
                onClick={clearFilters}
                className="flex-1 py-4 border border-gray-200 text-gray-600 rounded-2xl font-bold"
              >
                XÓA LỌC
              </button>
              <button
                type="button"
                onClick={() => setAdvanced(false)}
                className="flex-1 py-4 bg-red-600 text-white rounded-2xl font-bold shadow-lg shadow-red-200"
              >
                ÁP DỤNG
              </button>
            </>
          }
        >
          <div className="grid grid-cols-1 gap-4">
            <SelectBox label="Trạng thái" value={filters.status} options={statusOptions} onChange={(v) => updateFilter("status", v)} />
          </div>
        </MobileAdvancedFilterSheet>
      </div>
    );
  }

  // =============================================================================
  //                                   DESKTOP UI
  // =============================================================================
  return (
    <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm mb-6 text-left">
      <div className="flex items-center justify-between gap-4">
        {/* LEFT: Search & Action Buttons */}
        <div className="flex items-center gap-3 flex-1">
          <div className="relative w-full max-w-sm group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors" size={18} />
            <input
              type="text"
              placeholder="Tìm theo tên hoặc mã môn học..."
              value={filters.keyword}
              onChange={(e) => updateFilter("keyword", e.target.value)}
              className="w-full bg-gray-50 border-transparent border-2 focus:bg-white focus:border-red-500 pl-11 pr-4 py-2.5 rounded-2xl outline-none transition-all font-medium text-gray-700"
            />
          </div>

          <ActionButton icon={RotateCw} onClick={() => onFilterChange?.({ ...filters })} title="Làm mới" />
          <ActionButton icon={SlidersHorizontal} onClick={() => setAdvanced(!advanced)} active={advanced} title="Lọc nâng cao" />

          {isFiltering && (
            <button onClick={clearFilters} className="text-xs font-bold text-red-500 hover:text-red-700 flex items-center gap-1 uppercase tracking-wider transition-all ml-2">
              <Eraser size={14} /> Xóa lọc
            </button>
          )}
        </div>

        {/* Nút Thêm mới */}
        <div className="flex items-center gap-3">
          {hasPermission("elearning.subjects", "C") && (
            <button
              onClick={onNewClick}
              className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-red-700 to-red-800 text-white rounded-full font-bold hover:shadow-lg hover:shadow-red-100 transition-all active:scale-95 uppercase text-xs tracking-wider"
            >
              <Plus size={18} />
              Thêm môn học
            </button>
          )}

          <div className="relative">
            <button
              onClick={() => setMoreOpen(!moreOpen)}
              className={`h-11 w-11 flex items-center justify-center rounded-xl border transition-all ${
                moreOpen
                  ? "bg-gray-100 border-gray-300"
                  : "bg-white border-gray-200 text-gray-500 hover:bg-gray-50"
              }`}
            >
              <MoreVertical size={18} />
            </button>

            <AnimatePresence>
              {moreOpen && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 10 }}
                  className="absolute right-0 z-50 mt-3 w-56 overflow-hidden rounded-[1.5rem] border border-gray-100 bg-white p-2 shadow-2xl"
                  onMouseLeave={() => setMoreOpen(false)}
                >
                  <button
                    className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-xs font-bold text-gray-600 transition-all hover:bg-red-50 hover:text-red-600"
                    onClick={() => {
                      openGuide();
                      setMoreOpen(false);
                    }}
                  >
                    <BookOpen size={16} />
                    HƯỚNG DẪN SỬ DỤNG
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* ADVANCED FILTER DESKTOP */}
      <AnimatePresence>
        {advanced && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-visible"
          >
            <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-5 gap-4 mt-6 pt-6 border-t border-gray-100">
              <SelectBox
                label="Trạng thái sử dụng"
                value={filters.status}
                options={statusOptions}
                onChange={(v) => updateFilter("status", v)}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SubjectFilter;